import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { getSelectionContext } from "./source";
import type { PreviewAnnotation, RenderedTextAnnotationTarget } from "./types";
import { useAnnotationContextOptional } from "./AnnotationProvider";

type HighlightConstructor = new (...ranges: Range[]) => unknown;
type HighlightRegistry = {
    set: (name: string, highlight: unknown) => void;
    delete: (name: string) => void;
};

export interface RenderedTextSelection {
    blockId: string;
    outputHash?: string;
    startOffset: number;
    endOffset: number;
    quote: string;
    prefix?: string;
    suffix?: string;
    rect: DOMRect;
}

export interface RenderedTextPointHit {
    annotation: PreviewAnnotation;
    rect: DOMRect;
}

export function AnnotationRenderedTextScope({
    blockId,
    outputHash,
    inline = false,
    children,
}: {
    blockId: string;
    outputHash?: string;
    inline?: boolean;
    children: ReactNode;
}) {
    const context = useAnnotationContextOptional();
    const rootRef = useRef<HTMLElement | null>(null);
    const setRootRef = (node: HTMLElement | null) => {
        rootRef.current = node;
    };
    const annotations = useMemo(
        () =>
            context?.annotations.filter((annotation) =>
                annotation.status === "open" &&
                annotation.target.kind === "rendered-text" &&
                annotation.target.documentPath === context.documentPath &&
                annotation.target.blockId === blockId,
            ) ?? [],
        [blockId, context],
    );
    const styleText = useMemo(
        () => annotations.map((annotation) => renderedHighlightRule(annotation)).join("\n"),
        [annotations],
    );

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        return applyRenderedTextHighlights(root, annotations);
    }, [annotations]);

    const props = {
        "data-md-rendered-text-root": "true",
        "data-md-rendered-text-block-id": blockId,
        "data-md-rendered-output-hash": outputHash,
        className: inline ? "magic-md-rendered-text-scope-inline" : "magic-md-rendered-text-scope",
    };

    return (
        <>
            {styleText ? <style>{styleText}</style> : null}
            {inline ? (
                <span ref={setRootRef} {...props}>{children}</span>
            ) : (
                <div ref={setRootRef} {...props}>{children}</div>
            )}
        </>
    );
}

export function readRenderedTextSelection(rootBoundary: HTMLElement): RenderedTextSelection | null {
    const selection = rootBoundary.ownerDocument.defaultView?.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
    const range = selection.getRangeAt(0);
    if (!rootBoundary.contains(range.commonAncestorContainer)) return null;
    const startRoot = findRenderedTextRoot(range.startContainer);
    const endRoot = findRenderedTextRoot(range.endContainer);
    if (!startRoot || !endRoot || startRoot !== endRoot) return null;
    if (!rootBoundary.contains(startRoot)) return null;
    const blockId = startRoot.dataset.mdRenderedTextBlockId;
    if (!blockId) return null;
    const outputHash = startRoot.dataset.mdRenderedOutputHash;
    const startOffset = getTextOffset(startRoot, range.startContainer, range.startOffset);
    const endOffset = getTextOffset(startRoot, range.endContainer, range.endOffset);
    if (startOffset == null || endOffset == null) return null;
    const content = getRenderedTextContent(startRoot);
    const from = Math.max(0, Math.min(startOffset, endOffset, content.length));
    const to = Math.max(0, Math.min(Math.max(startOffset, endOffset), content.length));
    if (to <= from) return null;
    const context = getSelectionContext(content, from, to);
    return {
        blockId,
        outputHash,
        startOffset: from,
        endOffset: to,
        quote: context.quote || selection.toString(),
        prefix: context.prefix,
        suffix: context.suffix,
        rect: getRangeRect(range),
    };
}

export function findRenderedTextAnnotationAtPoint({
    rootBoundary,
    annotations,
    documentPath,
    clientX,
    clientY,
}: {
    rootBoundary: HTMLElement;
    annotations: PreviewAnnotation[];
    documentPath: string;
    clientX: number;
    clientY: number;
}): RenderedTextPointHit | null {
    const targetNode = rootBoundary.ownerDocument.elementFromPoint?.(clientX, clientY) ?? null;
    const root = targetNode ? findRenderedTextRoot(targetNode) : null;
    if (!root || !rootBoundary.contains(root)) return null;
    const blockId = root.dataset.mdRenderedTextBlockId;
    if (!blockId) return null;
    const candidates = annotations.filter((annotation) =>
        annotation.status === "open" &&
        annotation.target.kind === "rendered-text" &&
        annotation.target.documentPath === documentPath &&
        annotation.target.blockId === blockId,
    );
    if (candidates.length === 0) return null;

    const offset = getRenderedTextOffsetFromPoint(root, clientX, clientY);
    const annotation = offset == null
        ? (candidates.length === 1 ? candidates[0] : null)
        : candidates.find((item) => {
            const target = item.target as RenderedTextAnnotationTarget;
            return offset >= target.startOffset && offset <= target.endOffset;
        }) ?? null;
    if (!annotation || annotation.target.kind !== "rendered-text") return null;
    const range = createTextRange(root, annotation.target.startOffset, annotation.target.endOffset);
    return {
        annotation,
        rect: range ? getRangeRect(range) : root.getBoundingClientRect(),
    };
}

export function findRenderedTextRoot(node: Node): HTMLElement | null {
    const start = node.nodeType === Node.ELEMENT_NODE
        ? (node as HTMLElement)
        : node.parentElement;
    return start?.closest("[data-md-rendered-text-root]") ?? null;
}

export function createTextRange(
    root: HTMLElement,
    startOffset: number,
    endOffset: number,
): Range | null {
    const ownerDocument = root.ownerDocument;
    const start = findTextPosition(root, startOffset);
    const end = findTextPosition(root, endOffset);
    if (!start || !end) return null;
    const range = ownerDocument.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
}

function applyRenderedTextHighlights(
    root: HTMLElement,
    annotations: PreviewAnnotation[],
) {
    const api = getHighlightApi();
    if (!api) return undefined;
    const names: string[] = [];
    for (const annotation of annotations) {
        if (annotation.target.kind !== "rendered-text") continue;
        const visualRange = trimRenderedTextVisualRange(annotation.target);
        const range = createTextRange(
            root,
            visualRange.startOffset,
            visualRange.endOffset,
        );
        if (!range) continue;
        const name = renderedHighlightName(annotation.id);
        api.registry.set(name, new api.Highlight(range));
        names.push(name);
    }
    return () => {
        for (const name of names) {
            api.registry.delete(name);
        }
    };
}

function getHighlightApi(): { registry: HighlightRegistry; Highlight: HighlightConstructor } | null {
    const css = (globalThis as unknown as { CSS?: { highlights?: HighlightRegistry } }).CSS;
    const Highlight = (globalThis as unknown as { Highlight?: HighlightConstructor }).Highlight;
    if (!css?.highlights || !Highlight) return null;
    return { registry: css.highlights, Highlight };
}

function renderedHighlightRule(annotation: PreviewAnnotation): string {
    const name = renderedHighlightName(annotation.id);
    if (annotation.style.kind === "underline") {
        return `::highlight(${name}) { text-decoration-line: underline; text-decoration-style: wavy; text-decoration-color: color-mix(in srgb, #2563eb 72%, transparent); text-decoration-thickness: 1.35px; text-underline-offset: 0.28em; text-decoration-skip-ink: auto; }`;
    }
    if (annotation.style.kind === "comment") {
        return `::highlight(${name}) { background: color-mix(in srgb, #14b8a6 9%, transparent); text-decoration-line: underline; text-decoration-style: dotted; text-decoration-color: color-mix(in srgb, #0f766e 46%, transparent); text-decoration-thickness: 1px; text-underline-offset: 0.2em; text-decoration-skip-ink: auto; }`;
    }
    return `::highlight(${name}) { background: color-mix(in srgb, #facc15 34%, transparent); }`;
}

function renderedHighlightName(id: string): string {
    return `magic-md-rendered-${id.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
}

export function trimRenderedTextVisualRange(target: RenderedTextAnnotationTarget): {
    startOffset: number;
    endOffset: number;
} {
    const leading = target.quote.match(/^\s+/)?.[0].length ?? 0;
    const trailing = target.quote.match(/\s+$/)?.[0].length ?? 0;
    return {
        startOffset: Math.min(target.endOffset, target.startOffset + leading),
        endOffset: Math.max(target.startOffset, target.endOffset - trailing),
    };
}

function getRenderedTextOffsetFromPoint(
    root: HTMLElement,
    clientX: number,
    clientY: number,
): number | null {
    const doc = root.ownerDocument;
    const caretPositionFromPoint = (doc as unknown as {
        caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    }).caretPositionFromPoint;
    if (caretPositionFromPoint) {
        const position = caretPositionFromPoint.call(doc, clientX, clientY);
        if (position && root.contains(position.offsetNode)) {
            return getTextOffset(root, position.offsetNode, position.offset);
        }
    }
    const caretRangeFromPoint = (doc as unknown as {
        caretRangeFromPoint?: (x: number, y: number) => Range | null;
    }).caretRangeFromPoint;
    if (caretRangeFromPoint) {
        const range = caretRangeFromPoint.call(doc, clientX, clientY);
        if (range && root.contains(range.startContainer)) {
            return getTextOffset(root, range.startContainer, range.startOffset);
        }
    }
    return null;
}

function getTextOffset(root: HTMLElement, container: Node, offset: number): number | null {
    if (!root.contains(container)) return null;
    if (container.nodeType === Node.TEXT_NODE) {
        const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
            acceptNode(node) {
                return shouldSkipTextNode(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
            },
        });
        let total = 0;
        while (walker.nextNode()) {
            const text = walker.currentNode as Text;
            if (text === container) {
                return total + Math.max(0, Math.min(offset, text.data.length));
            }
            total += text.data.length;
        }
    }
    const range = root.ownerDocument.createRange();
    range.selectNodeContents(root);
    try {
        range.setEnd(container, offset);
    } catch {
        return null;
    }
    return range.toString().length;
}

function findTextPosition(
    root: HTMLElement,
    rawOffset: number,
): { node: Text; offset: number } | null {
    const target = Math.max(0, rawOffset);
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            return shouldSkipTextNode(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
        },
    });
    let remaining = target;
    let boundary: { node: Text; offset: number } | null = null;
    let lastText: Text | null = null;
    while (walker.nextNode()) {
        const text = walker.currentNode as Text;
        lastText = text;
        const length = text.data.length;
        if (remaining < length) {
            return { node: text, offset: remaining };
        }
        if (remaining === length) {
            boundary = { node: text, offset: length };
            remaining = 0;
            continue;
        }
        remaining -= length;
    }
    if (boundary) return boundary;
    if (lastText) return { node: lastText, offset: lastText.data.length };
    return null;
}

function shouldSkipTextNode(node: Node): boolean {
    const parent = node.parentElement;
    return !!parent?.closest("style,script,noscript,button,input,textarea,select,option,svg,canvas,[data-md-annotation-ui]");
}

export function getRenderedTextContent(root: HTMLElement): string {
    const parts: string[] = [];
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            return shouldSkipTextNode(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
        },
    });
    while (walker.nextNode()) {
        parts.push((walker.currentNode as Text).data);
    }
    return parts.join("");
}

function getRangeRect(range: Range): DOMRect {
    const rect = range.getBoundingClientRect?.();
    if (rect && (rect.width > 0 || rect.height > 0)) return rect;
    return {
        x: 0,
        y: 0,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        width: 0,
        height: 0,
        toJSON: () => ({}),
    } as DOMRect;
}
